import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert, MenuItem, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import bgImage from "assets/images/banner.png";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { Card } from "@mui/material";

function SecondSignUp() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        fullName: "",
        mobileNumber: "",
        email: "",
        companyName: "",
        howDidYouHearAboutUs: "",
        forWhom: "",
        forWhomOtherText: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    /* ================= VALIDATORS ================= */
    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const isValidMobile = (mobile) =>
        /^[0-9]{10}$/.test(mobile);

    /* ================= HANDLERS ================= */
    const handleChange = (e) => {
        const { name, value } = e.target;

        // mobile: allow only digits & max 10
        if (name === "mobileNumber" && !/^\d{0,10}$/.test(value)) return;

        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: "", severity: "info" });
    };

    /* ================= FORM VALIDATION ================= */
    const validateForm = () => {
        let newErrors = {};

        if (!formData.fullName.trim())
            newErrors.fullName = "Full Name is required";

        if (!formData.mobileNumber)
            newErrors.mobileNumber = "Mobile Number is required";
        else if (!isValidMobile(formData.mobileNumber))
            newErrors.mobileNumber = "Mobile Number must be 10 digits";

        if (!formData.email)
            newErrors.email = "Email is required";
        else if (!isValidEmail(formData.email))
            newErrors.email = "Invalid email address";

        if (!formData.companyName.trim())
            newErrors.companyName = "Company Name is required";

        if (!formData.howDidYouHearAboutUs)
            newErrors.howDidYouHearAboutUs = "This field is required";

        if (!formData.forWhom)
            newErrors.forWhom = "This field is required";

        if (
            formData.forWhom === "Other" &&
            !formData.forWhomOtherText.trim()
        )
            newErrors.forWhomOtherText = "Please specify";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ================= SUBMIT ================= */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setSnackbar({
                open: true,
                message: "Please fix the errors before submitting.",
                severity: "error",
            });
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                "https://api.exampladmedia.com/api/request/registerEntries",
                formData,
                { headers: { "Content-Type": "application/json" } }
            );

            setSnackbar({
                open: true,
                message:
                    response.data.message ||
                    "One of our representatives will be reaching out to you shortly.",
                severity: "success",
            });

            setFormData({
                fullName: "",
                mobileNumber: "",
                email: "",
                companyName: "",
                howDidYouHearAboutUs: "",
                forWhom: "",
                forWhomOtherText: "",
            });

            setTimeout(() => {
                navigate("/authentication/sign-in", { replace: true });
            }, 1500);
        } catch (error) {
            setSnackbar({
                open: true,
                message:
                    error.response?.data?.message ||
                    "Something went wrong! Please try again.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                backgroundImage: `url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center 20%",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <BasicLayout>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
        <Card>

                <MDBox pt={2} pb={3}>
                    <MDBox
                        variant="gradient"
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="info"
                        mx={2}
                        mt={-3}
                        p={2}
                        mb={1}
                        textAlign="center"
                    >
                        <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                            Sign Up
                        </MDTypography>
                    </MDBox>
                    <MDBox pt={4} pb={3} px={3}>
                        <MDBox component="form" role="form" onSubmit={handleSubmit}>
                            <MDBox mb={1}>
                                <MDInput
                                    label="Full Name"
                                    name="fullName"
                                    fullWidth
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName}
                                    required
                                />
                            </MDBox>

                            <MDBox mb={1}>
                                <MDInput
                                    label="Mobile Number"
                                    name="mobileNumber"
                                    fullWidth
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    error={!!errors.mobileNumber}
                                    helperText={errors.mobileNumber}
                                    required
                                />
                            </MDBox>

                            <MDBox mb={1}>
                                <MDInput
                                    type="email"
                                    label="Email Address"
                                    name="email"
                                    fullWidth
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    required
                                />
                            </MDBox>

                            <MDBox mb={1}>
                                <MDInput
                                    label="Company Name"
                                    name="companyName"
                                    fullWidth
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    error={!!errors.companyName}
                                    helperText={errors.companyName}
                                    required
                                />
                            </MDBox>

                            <MDBox mb={1}>
                                <TextField
                                    select
                                    fullWidth
                                    label="How Did You Hear About Us?"
                                    name="howDidYouHearAboutUs"
                                    value={formData.howDidYouHearAboutUs}
                                    onChange={handleChange}
                                    error={!!errors.howDidYouHearAboutUs}
                                    helperText={errors.howDidYouHearAboutUs}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: "40px",          // ✅ control height here
                                            alignItems: "center",
                                        },
                                        "& .MuiSelect-select": {
                                            display: "flex",
                                            alignItems: "center",
                                            paddingY: 0,             // keeps text vertically centered
                                        },
                                    }}
                                >
                                    <MenuItem value="Google">Google</MenuItem>
                                    <MenuItem value="Social Media">Social Media</MenuItem>
                                    <MenuItem value="Referral">Referral</MenuItem>
                                    <MenuItem value="Online Advertisement">Online Advertisement</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </TextField>
                            </MDBox>

                            <MDBox mb={1}>
                                <TextField
                                    select
                                    fullWidth
                                    label="For whom are you looking for Digital PR services?"
                                    name="forWhom"
                                    value={formData.forWhom}
                                    onChange={handleChange}
                                    error={!!errors.forWhom}
                                    helperText={errors.forWhom}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            height: "40px",          // ✅ control height here
                                            alignItems: "center",
                                        },
                                        "& .MuiSelect-select": {
                                            display: "flex",
                                            alignItems: "center",
                                            paddingY: 0,             // keeps text vertically centered
                                        },
                                    }}
                                >
                                    <MenuItem value="Self">Self</MenuItem>
                                    <MenuItem value="Company">Company</MenuItem>
                                    <MenuItem value="Resell">Resell</MenuItem>
                                    <MenuItem value="Link Building">Link Building</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </TextField>
                            </MDBox>

                            {formData.forWhom === "Other" && (
                                <MDBox mb={1}>
                                    <MDInput
                                        label="Please specify"
                                        name="forWhomOtherText"
                                        fullWidth
                                        value={formData.forWhomOtherText}
                                        onChange={handleChange}
                                        error={!!errors.forWhomOtherText}
                                        helperText={errors.forWhomOtherText}
                                    />
                                </MDBox>
                            )}

                            <MDBox mt={3} display="flex" gap={1}>
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    fullWidth
                                    type="button"
                                    onClick={() => navigate("/authentication/sign-in", { replace: true })}
                                >
                                    Back
                                </MDButton>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    fullWidth
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? "Submitting..." : "Sign Up"}
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </MDBox>
        </Card>
            </BasicLayout>
        </div>
    );
}

export default SecondSignUp;
